import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, useLocation } from "wouter";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from "./contexts/AuthContext";
import { SpeakerApplicationProvider } from "./contexts/SpeakerApplicationContext";
import ChatWidget from "./components/ChatWidget";
import Home from "./pages/Home";
import MissionValues from "./pages/MissionValues";
import FractionalServices from "./pages/FractionalServices";
import Community from "./pages/Community";
import CollaborateWithUs from "./pages/CollaborateWithUs";
import FeaturedSpeakers from "./pages/FeaturedSpeakers";
import CalendarOfEvents from "./pages/CalendarOfEvents";
import MyEvents from "./pages/MyEvents";
import EventTicket from "./pages/EventTicket";
import EventCheckIn from "./pages/EventCheckIn";
import MyProfile from "./pages/MyProfile";
import EventDetail from "@/pages/EventDetail";
import LoginPage from "@/pages/LoginPage";
import CreateAccount from "@/pages/CreateAccount";
import MemberDirectory from "@/pages/MemberDirectory";
import AdminCRM from "@/pages/AdminCRM";
import RequireAdmin from "@/components/RequireAdmin";
import RequirePaidMember from "@/components/RequirePaidMember";
import PremiumUpgrade from "@/pages/PremiumUpgrade";
import EventDiscoveryCanvases from "@/pages/EventDiscoveryCanvases";
import CalendarLayoutConcepts from "@/pages/CalendarLayoutConcepts";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Disclaimer from "@/pages/Disclaimer";
import SpeakerPortal from "@/pages/SpeakerPortal";
import MemberDestination from "@/pages/MemberDestination";
import CommunityNewsletterConsent from "@/pages/CommunityNewsletterConsent";
import SponsorPage from "@/pages/SponsorPage";
import EventTickets from "@/pages/EventTickets";
import MyEventTickets from "@/pages/MyEventTickets";
import TicketClaim from "@/pages/TicketClaim";
import GuestTickets from "@/pages/GuestTickets";
function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <>
      <ScrollToTop />
      <Switch>
      <Route path="/" component={Home} />
      <Route path="/mission-and-values" component={MissionValues} />
      <Route path="/hr-services" component={FractionalServices} />
      <Route path="/fractional-services" component={FractionalServices} />
      <Route path="/community">
        <RequirePaidMember><Community /></RequirePaidMember>
      </Route>
      <Route path="/community/newsletter/confirm">
        <CommunityNewsletterConsent mode="confirm" />
      </Route>
      <Route path="/community/newsletter/unsubscribe">
        <CommunityNewsletterConsent mode="unsubscribe" />
      </Route>
      <Route path="/collaborate-with-us" component={CollaborateWithUs} />
       <Route path="/sponsors/:slug" component={SponsorPage} />
      <Route path="/speakers" component={FeaturedSpeakers} />
      <Route path="/featured-speakers" component={FeaturedSpeakers} />
      <Route path="/calendar-of-events" component={CalendarOfEvents} />
      <Route path="/calendar-concepts" component={CalendarLayoutConcepts} />
      <Route path="/calendar-concepts/:concept" component={CalendarLayoutConcepts} />
      <Route path="/privacy-policy" component={PrivacyPolicy} />
      <Route path="/disclaimer" component={Disclaimer} />
      <Route path="/event-concepts/:canvas" component={EventDiscoveryCanvases} />
      <Route path="/my-events/ticket/:slug" component={EventTicket} />
      <Route path="/event-check-in"><RequireAdmin><EventCheckIn /></RequireAdmin></Route>
      <Route path="/my-events/:slug/tickets" component={MyEventTickets} />
      <Route path="/my-events" component={MyEvents} />
      <Route path="/account/events" component={MyEvents} />
      <Route path="/tickets/claim" component={TicketClaim} />
      <Route path="/guest-tickets" component={GuestTickets} />
      <Route path="/my-profile" component={MyProfile} />
      <Route path="/events/:slug/tickets" component={EventTickets} />
      <Route path="/events/:slug" component={EventDetail} />
      <Route path="/login" component={LoginPage} />
      <Route path="/create-account" component={CreateAccount} />
      <Route path="/member-directory" component={MemberDirectory} />
      <Route path="/resources"><MemberDestination destination="resources" /></Route>
      <Route path="/messages"><MemberDestination destination="messages" /></Route>
      <Route path="/groups"><MemberDestination destination="groups" /></Route>
      <Route path="/micro-learning"><MemberDestination destination="micro-learning" /></Route>
      <Route path="/support"><MemberDestination destination="support" /></Route>
      <Route path="/billing"><MemberDestination destination="billing" /></Route>
      {/*
        Every admin screen goes inside this guard, including any added later.
        A route placed outside it is an admin surface that renders before the
        role is known — which is the mistake the guard exists to prevent.
      */}
      <Route path="/admin">
        <RequireAdmin><AdminCRM /></RequireAdmin>
      </Route>
      <Route path="/admin/:rest*">
        <RequireAdmin><NotFound /></RequireAdmin>
      </Route>
      <Route path="/speaker-portal" component={SpeakerPortal} />
      <Route path="/membership" component={PremiumUpgrade} />
      <Route path="/premium" component={PremiumUpgrade} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function AppShell() {
  const [location] = useLocation();
  const hideChat = location.startsWith("/admin") || location === "/login" || location === "/speaker-portal";
  return (
    <>
      <Router />
      {!hideChat && <ChatWidget />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <AuthProvider>
          <SpeakerApplicationProvider>
            <TooltipProvider>
              <Toaster />
              <AppShell />
            </TooltipProvider>
          </SpeakerApplicationProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
